/**
 * Общий рендер поля формы и привязка поля-справочника (ref).
 *
 * Вынесено из components/modal.js (УИ-2a), чтобы один и тот же код формы
 * использовали и центральная модалка (openModal), и правая панель записи
 * (components/record-panel.js) — без дубликата.
 *
 * Схема поля: { name, label, type, required?, options?, placeholder?, hint?,
 *               wide?, readonly?, value?, min?, max?, step?, maxlength?, rows?,
 *               refId?, picker? }
 *   type ∈ text | number | date | select | textarea | ref  (чекбокса нет —
 *   boolean передаётся как select «0/1»).
 *   wide     — поле на всю ширину сетки (.modal-field-wide); нужно для textarea.
 *   readonly — поле только для просмотра (disabled; у ref — без кнопок выбора).
 *   hint     — подсказка под полем.
 *
 * ref: видимый текстовый input name + скрытый name_id + статус связи и кнопки
 * «из справочника…» / «отвязать». Пикер открывается ПОВЕРХ (native <dialog>);
 * *_id и текст пишет bindRefField через picker(currentId) → {action,id,name}.
 * Стили .ref-link* — в styles/counterparties.css.
 */

export function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
export function escapeAttr(s) {
  return String(s).replace(/"/g, '&quot;');
}

/** Поле → HTML (label + контрол). */
export function renderField(f) {
  const id = `modal-field-${f.name}`;
  const wide = f.wide ? ' modal-field-wide' : '';
  const dis = f.readonly ? ' disabled' : '';
  const hint = f.hint ? `<span class="modal-field-hint">${escapeHtml(String(f.hint))}</span>` : '';

  if (f.type === 'select') {
    const opts = (f.options ?? []).map((o) => {
      const value = typeof o === 'string' ? o : o.value;
      const label = typeof o === 'string' ? o : (o.label ?? o.value);
      const sel = String(f.value ?? '') === String(value) ? ' selected' : '';
      return `<option value="${escapeAttr(String(value))}"${sel}>${escapeHtml(label)}</option>`;
    }).join('');
    return `
      <label class="modal-field${wide}">
        <span class="modal-field-label">${escapeHtml(f.label)}</span>
        <select id="${id}" name="${f.name}"${f.required ? ' required' : ''}${dis} class="modal-select">${opts}</select>
        ${hint}
      </label>`;
  }

  // ref — текст (совместимость) + связь со справочником (СПР-1b).
  if (f.type === 'ref') {
    const idVal = f.refId != null ? escapeAttr(String(f.refId)) : '';
    const status = f.refId ? '✓ из справочника' : (f.value ? 'не связан' : '');
    const statusCls = f.refId ? 'is-linked' : (f.value ? 'is-unlinked' : '');
    const controls = f.readonly ? '' : `
            <span class="ref-link">
              <span class="ref-link-status ${statusCls}" data-ref-status="${f.name}">${status}</span>
              <button type="button" class="dt-link" data-ref-pick="${f.name}">из справочника…</button>
              <button type="button" class="dt-link" data-ref-unlink="${f.name}"${f.refId ? '' : ' hidden'}>отвязать</button>
            </span>`;
    return `
      <label class="modal-field${wide}">
        <span class="modal-field-label">${escapeHtml(f.label)}</span>
        <input id="${id}" name="${f.name}" type="text"${f.value != null ? ` value="${escapeAttr(String(f.value))}"` : ''}${f.placeholder ? ` placeholder="${escapeAttr(f.placeholder)}"` : ''}${dis} />
        <input type="hidden" name="${f.name}_id" value="${idVal}" />${controls}
        ${hint}
      </label>`;
  }

  if (f.type === 'textarea') {
    return `
      <label class="modal-field${wide}">
        <span class="modal-field-label">${escapeHtml(f.label)}</span>
        <textarea id="${id}" name="${f.name}" rows="${f.rows ?? 8}" class="modal-textarea"${f.required ? ' required' : ''}${dis}${f.placeholder ? ` placeholder="${escapeAttr(f.placeholder)}"` : ''}>${escapeHtml(String(f.value ?? ''))}</textarea>
        ${hint}
      </label>`;
  }

  const inputAttrs = [
    `id="${id}"`,
    `name="${f.name}"`,
    `type="${f.type ?? 'text'}"`,
    f.placeholder ? `placeholder="${escapeAttr(f.placeholder)}"` : '',
    f.value != null ? `value="${escapeAttr(String(f.value))}"` : '',
    f.min != null ? `min="${f.min}"` : '',
    f.max != null ? `max="${f.max}"` : '',
    f.step != null ? `step="${f.step}"` : '',
    f.maxlength != null ? `maxlength="${f.maxlength}"` : '',
    f.required ? 'required' : '',
    f.readonly ? 'disabled' : '',
  ].filter(Boolean).join(' ');
  return `
    <label class="modal-field${wide}">
      <span class="modal-field-label">${escapeHtml(f.label)}</span>
      <input ${inputAttrs} />
      ${hint}
    </label>`;
}

/**
 * Привязать поле-справочник внутри scope (dialog или панель) к пикеру.
 * После выбора/отвязки возвращает фокус в текстовое поле — чтобы следующий
 * Esc не закрыл панель (образец — tender-inspector + ref-picker).
 */
export function bindRefField(scope, f, { onChange } = {}) {
  if (f.type !== 'ref' || typeof f.picker !== 'function') return;
  const textEl = scope.querySelector(`[name="${f.name}"]`);
  const idEl = scope.querySelector(`[name="${f.name}_id"]`);
  const statusEl = scope.querySelector(`[data-ref-status="${f.name}"]`);
  const pickBtn = scope.querySelector(`[data-ref-pick="${f.name}"]`);
  const unlinkBtn = scope.querySelector(`[data-ref-unlink="${f.name}"]`);
  if (!textEl || !idEl || !pickBtn) return;   // readonly-ref: кнопок нет

  const setLink = (id, name) => {
    idEl.value = id ?? '';
    if (id && name) textEl.value = name;
    const text = (textEl.value ?? '').trim();
    if (statusEl) {
      if (id) {
        statusEl.textContent = '✓ из справочника';
        statusEl.className = 'ref-link-status is-linked';
      } else {
        statusEl.textContent = text ? 'не связан' : '';
        statusEl.className = 'ref-link-status' + (text ? ' is-unlinked' : '');
      }
    }
    if (unlinkBtn) unlinkBtn.hidden = !id;
    onChange?.();
  };

  pickBtn.addEventListener('click', async () => {
    const res = await f.picker(idEl.value ? Number(idEl.value) : null);
    if (res) {
      if (res.action === 'unlink') setLink(null, null);
      else if (res.action === 'select') setLink(res.id, res.name);
    }
    textEl.focus();   // вернуть фокус в поле после закрытия пикера
  });
  unlinkBtn?.addEventListener('click', () => { setLink(null, null); textEl.focus(); });
}

/**
 * Собрать плоское тело { name: value } из scope по схеме fields.
 * '' → null; number → parseFloat; для ref добавляется name_id (число или null).
 */
export function collectFields(scope, fields) {
  const result = {};
  for (const f of fields) {
    const el = scope.querySelector(`[name="${f.name}"]`);
    if (!el) continue;
    const raw = el.value;
    if (f.type === 'number') {
      result[f.name] = raw === '' ? null : parseFloat(raw);
    } else {
      result[f.name] = raw === '' ? null : raw;
    }
    if (f.type === 'ref') {
      const idEl = scope.querySelector(`[name="${f.name}_id"]`);
      result[`${f.name}_id`] = idEl && idEl.value ? Number(idEl.value) : null;
    }
  }
  return result;
}
